import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import API from '../../axios/API';
import { DataContext } from '../DataProviders/DataProvider';

const NotificationContext = createContext();

const NotificationProvider = ({ children }) => {
  const [showNotification, setShowNotification] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const { loginCustomerId, token } = useContext(DataContext);
  const [notificationStatus, setNotificationStatus] = useState("All");
  const [notificationStatusLoading, setNotificationStatusLoading] = useState(false);
  const [notificationStatsData, setNotificationStatsData] = useState([]);

  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationData, setNotificationData] = useState([]);

  // Refs to track previous count and initial load state
  const previousUnreadCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);

  // Refs to prevent duplicate API calls
  const isFetchingStatsRef = useRef(false);
  const isFetchingNotificationsRef = useRef(false);

  // Function to play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      const audio = new Audio('https://inboxlogy-cdn.s3.amazonaws.com/notificationsound.wav');
      audio.play().catch(error => {
        console.log('Error playing notification sound:', error);
      });
    } catch (error) {
      console.log('Error creating audio:', error);
    }
  }, []);

  const notificationStats = useCallback(async () => {
    const hasValidToken = token && token !== 'null' && token !== 'undefined';
    const hasValidCustomerId = loginCustomerId && loginCustomerId !== 'null' && loginCustomerId !== 'undefined' && loginCustomerId.length !== 0;
    if (!hasValidToken || !hasValidCustomerId) {
      return;
    }

    // Prevent duplicate calls
    if (isFetchingStatsRef.current) return;

    try {
      isFetchingStatsRef.current = true;
      const response = await API.get(`${import.meta.env.VITE_BACKEND_API}/stats/${loginCustomerId}`)
      setNotificationStatsData(response.data)
      // Always set notificationCount to unread count only, regardless of current tab
      const unreadStats = response.data.find(item => item.status === "UNREAD");
      const currentUnreadCount = unreadStats?.count || 0;
      const previousUnreadCount = previousUnreadCountRef.current;

      // Play sound only if:
      // 1. It's not the initial load (we've already fetched data at least once)
      // 2. Current count is greater than previous count (new notifications arrived)
      if (!isInitialLoadRef.current && currentUnreadCount > previousUnreadCount) {
        playNotificationSound();
      }

      // Update the previous count for next comparison
      previousUnreadCountRef.current = currentUnreadCount;

      // Mark initial load as complete after first successful fetch
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }

      setNotificationCount(currentUnreadCount);
    } catch (error) {
      console.log(error)
    } finally {
      setNotificationStatusLoading(false);
      isFetchingStatsRef.current = false;
    }
  }, [loginCustomerId, token, playNotificationSound])

  const fetchNotificationData = useCallback(async () => {
    const hasValidToken = token && token !== 'null' && token !== 'undefined';
    const hasValidCustomerId = loginCustomerId && loginCustomerId !== 'null' && loginCustomerId !== 'undefined' && loginCustomerId.length !== 0;
    if (!hasValidToken || !hasValidCustomerId) {
      return;
    }

    // Prevent duplicate calls
    if (isFetchingNotificationsRef.current) return;

    try {
      isFetchingNotificationsRef.current = true;
      setNotificationStatusLoading(true);
      const response = await API.get(
        `${import.meta.env.VITE_BACKEND_API
        }/get-notification?customerId=${loginCustomerId}&status=${notificationStatus}&direction=desc`
      );
      // Transform the data to include isUnread property
      const transformedData = response.data.map(notification => ({
        ...notification,
        isUnread: notification.status === "UNREAD"
      }));

      // Don't update notificationCount here - it should always show unread count from stats
      setNotificationData(transformedData);
    } catch (error) {
      console.log(error)
    } finally {
      setNotificationStatusLoading(false);
      isFetchingNotificationsRef.current = false;
    }
  }, [notificationStatus, loginCustomerId, token])
  useEffect(() => {
    const hasValidToken = token && token !== 'null' && token !== 'undefined';
    const hasValidCustomerId = loginCustomerId && loginCustomerId !== 'null' && loginCustomerId !== 'undefined' && loginCustomerId.length !== 0;

    // Reset initial load flag and previous count when user changes
    isInitialLoadRef.current = true;
    previousUnreadCountRef.current = 0;

    if (!hasValidToken || !hasValidCustomerId) {
      return;
    }

    // Call immediately
    notificationStats()
    fetchNotificationData()

    // Set up interval for polling
    const interval = setInterval(() => {
      notificationStats() // Check for new notifications and play sound if count increased
      fetchNotificationData()
    }, 50000)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationStatus, loginCustomerId, token])


  const markAsRead = useCallback(async (notificationId) => {
    const hasValidToken = token && token !== 'null' && token !== 'undefined';
    if (!hasValidToken) return;
    try {
      const response = await API.put(`${import.meta.env.VITE_BACKEND_API
        }/${notificationId}/read`)
      if (response.status === 200) {
        notificationStats()
        fetchNotificationData()
      }
    } catch (error) {
      console.log(error)
    }
  }, [fetchNotificationData, notificationStats, token])


  const handleMarkAllAsRead = useCallback(async () => {
    const hasValidToken = token && token !== 'null' && token !== 'undefined';
    const hasValidCustomerId = loginCustomerId && loginCustomerId !== 'null' && loginCustomerId !== 'undefined' && loginCustomerId.length !== 0;
    if (!hasValidToken || !hasValidCustomerId) {
      return;
    }
    try {
      const response = await API.put(`${import.meta.env.VITE_BACKEND_API
        }/notifications/${loginCustomerId}/read-all`)
      if (response.status === 200) {
        notificationStats()
        fetchNotificationData()
      }
    } catch (error) {
      console.log(error)
    }
  }, [fetchNotificationData, notificationStats, loginCustomerId, token])

  useEffect(() => {
    let timer;
    if (showNotification) {
      timer = setTimeout(() => {
        setShowNotification(false);
        setNotificationMessage('')
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [showNotification]);

  return (
    <NotificationContext.Provider value={{ showNotification, setShowNotification, notificationMessage, setNotificationMessage, notificationData, notificationCount, markAsRead, notificationStatus, setNotificationStatus, fetchNotificationData, notificationStatusLoading, notificationStatsData, handleMarkAllAsRead, notificationStats, setDrawer, drawer }}>
      {children}
    </NotificationContext.Provider>
  );
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { NotificationContext, NotificationProvider };

